// v1.28.80 — Remark Preset admin page. For each Payment Mode in this
// company, the Company Admin can pin a default remark that auto-fills the
// Remark field in Record Payment / Record Cash / Payment Out forms. The
// preset is stored on the payment_modes row (`default_remark`) so it's
// naturally company-scoped via the ScopedDB layer.
//
// v1.28.80 — When saving a preset that already has transactions using this
// mode, prompt the SU with a confirmation dialog offering to update the
// remark on every existing transaction that uses this mode, OR only apply
// going forward. The bulk update is audit-logged with old/new remark,
// mode name, timestamp, SU email and record count.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, ChatText, FloppyDisk, X, MagnifyingGlass, Warning } from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";

export default function RemarkPresets() {
    const [modes, setModes] = useState([]);
    const [drafts, setDrafts] = useState({}); // { [mode_id]: pending_preset_text }
    const [savingId, setSavingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    // v1.28.80 — Confirmation dialog state.
    const [cascadeDlg, setCascadeDlg] = useState(null);
    // { mode: {...}, oldPreset, newPreset, count, busy }

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/payment-modes");
            setModes(data || []);
            setDrafts({});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const currentValue = (m) =>
        drafts[m.id] !== undefined ? drafts[m.id] : (m.default_remark || "");

    const isDirty = (m) => {
        const draft = drafts[m.id];
        if (draft === undefined) return false;
        return (draft || "") !== (m.default_remark || "");
    };

    // Actually PATCH the mode. `applyToExisting` decides whether the new
    // preset text is cascaded onto every existing transaction using this
    // mode's remark field.
    const doPatch = async (m, preset, applyToExisting) => {
        try {
            const { data } = await api.patch(`/payment-modes/${m.id}`, {
                default_remark: preset,
                apply_to_existing: !!applyToExisting,
            });
            const updated = data?.txns_remark_updated || 0;
            if (applyToExisting) {
                toast.success(`Preset saved · ${updated} record${updated === 1 ? "" : "s"} updated`);
            } else {
                toast.success(preset
                    ? `Preset saved for "${m.name}" — old records unchanged`
                    : `Preset cleared for "${m.name}"`);
            }
            setModes((prev) => prev.map((x) => x.id === m.id ? { ...x, default_remark: preset } : x));
            setDrafts((d) => { const { [m.id]: _dropped, ...rest } = d; return rest; });
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Called when the row's Save button is clicked.
    const onSaveClick = async (m) => {
        const preset = (currentValue(m) || "").trim();
        const oldPreset = (m.default_remark || "").trim();
        setSavingId(m.id);
        try {
            // If clearing, or no change, just save without asking.
            if (!preset || preset === oldPreset) {
                await doPatch(m, preset, false);
                return;
            }
            // Peek at how many transactions currently carry this mode. If
            // 0, no need to ask — save straight through.
            let count = 0;
            try {
                const r = await api.get(`/payment-modes/${m.id}/usage-count`);
                count = r.data?.count || 0;
            } catch { /* soft-fail: fall back to always asking */ count = -1; }
            if (count === 0) {
                await doPatch(m, preset, false);
                return;
            }
            // Prompt the SU. Dialog buttons will finalise the PATCH.
            setCascadeDlg({ mode: m, oldPreset, newPreset: preset, count, busy: false });
        } finally { setSavingId(null); }
    };

    const confirmCascade = async (applyToExisting) => {
        if (!cascadeDlg) return;
        setCascadeDlg((d) => ({ ...d, busy: true }));
        await doPatch(cascadeDlg.mode, cascadeDlg.newPreset, applyToExisting);
        setCascadeDlg(null);
    };

    const clearDraft = (m) => setDrafts((d) => {
        const { [m.id]: _dropped, ...rest } = d; return rest;
    });

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        const rows = [...modes];
        rows.sort((a, b) => {
            const ap = (a.default_remark || "") ? 0 : 1;
            const bp = (b.default_remark || "") ? 0 : 1;
            if (ap !== bp) return ap - bp;
            return (a.name || "").localeCompare(b.name || "");
        });
        if (!query) return rows;
        return rows.filter((m) =>
            (m.name || "").toLowerCase().includes(query) ||
            (m.default_remark || "").toLowerCase().includes(query),
        );
    }, [modes, q]);

    const withPreset = modes.filter((m) => (m.default_remark || "").trim()).length;

    return (
        <div className="space-y-6" data-testid="remark-presets-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
                        <ChatText size={32} weight="fill" className="text-violet-700" />
                        Remark Presets
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-slate-600">
                        Pin a default remark to any Payment Mode. When you pick that mode in Record Payment, Record Cash or Payment Out,
                        the Remark field auto-fills with the preset text — which you can still edit or clear before saving.
                        Empty preset = Remark stays blank as before.
                    </p>
                </div>
                <Link
                    to="/admin/settings/payment-modes"
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                    <ArrowLeft size={12} /> Manage Payment Modes
                </Link>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px]">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            data-testid="remark-presets-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by mode name or preset text…"
                            className="pl-9"
                        />
                    </div>
                    <div className="text-xs text-slate-500 tabular-nums">
                        {withPreset} of {modes.length} modes have a preset
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {loading ? (
                    <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        title={q ? "No matching modes" : "No payment modes yet"}
                        description={q ? "Try a different search term." : "Create some Payment Modes first, then come back to pin presets."}
                    />
                ) : (
                    <table className="w-full text-sm" data-testid="remark-presets-table">
                        <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
                            <tr>
                                <th className="px-3 py-2 text-left w-1/3">Payment Mode</th>
                                <th className="px-3 py-2 text-left">Default Remark</th>
                                <th className="px-3 py-2 text-right w-[190px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((m) => {
                                const value = currentValue(m);
                                const dirty = isDirty(m);
                                const busy = savingId === m.id;
                                return (
                                    <tr key={m.id} data-testid={`remark-preset-row-${m.id}`} className={dirty ? "bg-amber-50/40" : ""}>
                                        <td className="px-3 py-2">
                                            <div className="font-semibold text-slate-800">{m.name}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                                used {m.usage_count || 0}×
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Input
                                                data-testid={`remark-preset-input-${m.id}`}
                                                value={value}
                                                onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                                                placeholder="e.g. Withdraw from Goodluck Portal"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && dirty && !busy) onSaveClick(m);
                                                    if (e.key === "Escape" && dirty) clearDraft(m);
                                                }}
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {dirty && (
                                                    <button
                                                        type="button"
                                                        onClick={() => clearDraft(m)}
                                                        disabled={busy}
                                                        data-testid={`remark-preset-cancel-${m.id}`}
                                                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                                                    >
                                                        <X size={11} /> Cancel
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => onSaveClick(m)}
                                                    disabled={!dirty || busy}
                                                    data-testid={`remark-preset-save-${m.id}`}
                                                    className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-semibold ${
                                                        dirty
                                                            ? "border-violet-300 bg-violet-600 text-white hover:bg-violet-700"
                                                            : "border-slate-200 bg-slate-50 text-slate-400"
                                                    } disabled:cursor-not-allowed`}
                                                >
                                                    <FloppyDisk size={11} weight="bold" /> {busy ? "Saving…" : "Save"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* v1.28.80 — Cascade confirmation dialog. */}
            {cascadeDlg && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
                     role="dialog" aria-modal="true"
                     data-testid="remark-cascade-modal">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                            <Warning size={18} weight="fill" className="text-amber-500" />
                            <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Update Existing Records?</div>
                        </div>
                        <div className="space-y-3 px-5 py-4 text-sm text-slate-700">
                            <div>
                                Do you want to apply this remark to <b className="text-slate-900" data-testid="remark-cascade-count">{cascadeDlg.count}</b> existing transaction{cascadeDlg.count === 1 ? "" : "s"} that use <b>{cascadeDlg.mode.name}</b>?
                            </div>
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
                                <div className="grid grid-cols-[80px_1fr] gap-y-1">
                                    <div className="font-semibold text-slate-500">Old remark</div>
                                    <div className="font-mono text-slate-700 break-words">{cascadeDlg.oldPreset || <em className="text-slate-400">(blank)</em>}</div>
                                    <div className="font-semibold text-slate-500">New remark</div>
                                    <div className="font-mono text-violet-700 break-words">{cascadeDlg.newPreset}</div>
                                </div>
                            </div>
                            <div className="text-[11px] text-slate-500">
                                Only the remark / description field is affected. The payment mode name on old records stays the same. This action is logged in the audit trail with the timestamp, your email, mode name, old remark, new remark and record count.
                            </div>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-5 py-3">
                            <button type="button"
                                    onClick={() => setCascadeDlg(null)}
                                    disabled={cascadeDlg.busy}
                                    data-testid="remark-cascade-abort"
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                Cancel
                            </button>
                            <button type="button"
                                    onClick={() => confirmCascade(false)}
                                    disabled={cascadeDlg.busy}
                                    data-testid="remark-cascade-forward-only"
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                No, apply going forward only
                            </button>
                            <button type="button"
                                    onClick={() => confirmCascade(true)}
                                    disabled={cascadeDlg.busy}
                                    data-testid="remark-cascade-update-all"
                                    className="rounded-md bg-violet-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50">
                                {cascadeDlg.busy ? "Updating…" : `Yes, update all ${cascadeDlg.count}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
